/* eslint-disable indent */
import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useState } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { Select } from '@/components/Controls/Select';
import { FormManagementProps } from '@/type/form.types';
import { useGetGroupsSelectOptions } from '@/hooks/group.hooks';
import { useGetCompaniesSelectOptions } from '@/hooks/company.hooks';
import { useGetBranchesSelectOptions } from '@/hooks/branch.hooks';
import {
  useCreateVehicule,
  useGetExistingVehicule,
  useGetVehiculesSelectOptions,
} from '@/hooks/vehicule.hooks';
import { Switch } from '@/components/Controls/Switch';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { NotFound } from '@/components/Message/NotFound';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import {
  EMAIL_REGEX,
  FRENCH_LICENSE_PLATE_NUMBER_REGEX,
  PHONE_REGEX,
  SPACE_REGEX,
  VIN_REGEX,
} from '@/utils/regex.utils';
import { Input } from '@/components/Controls/Input';
import Loader from '@/components/Loading/Loader';
import { FieldError } from '@/type/error.types';
import { FormSectionTitle } from '@/components/Form/FormSectionTitle';
import { useGetVehiculeCheckTypesSelectOptions } from '@/hooks/vehicule-check-type.hooks';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { useParams } from 'react-router-dom';
import { generateRandomUUID } from '@/utils/random-uuid.utils';
import { DateTimePicker } from '@/components/Custom/DateTimePicker';
import dayjs from 'dayjs';
import { ErrorMessage } from '@/components/Message/ErrorMessage';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { Trans } from 'react-i18next';
import { showModal } from '@/utils/modal.utils';
import { Textarea } from '@/components/Controls/Textarea';
// import { SelectAutoComplete } from '@/components/Controls/SelectAutoComplete';

export const VehiculeCheckRequestManagement = <TSchema extends FieldValues>({
  t,
  data,
  isReadOnly,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const { loggedInUser } = useAuth();

  const { vehiculeId } = useParams();

  const [assignedToVehicule, setAssignedToVehicule] = useState(
    Boolean(vehiculeId) || Boolean(data?.vehiculeId),
  );
  const [addAdditionalEmails, setAddAdditionalEmails] = useState<boolean>(
    Boolean(data?.additionalEmails?.length > 0),
  );
  const [licensePlateNumberValue, setLicensePlateNumberValue] = useState('');
  const [vinValue, setVinValue] = useState('');
  const [licensePlateNumberError, setLicensePlateNumberError] = useState('');
  const [vinError, setVinError] = useState('');
  const [brandValue, setBrandValue] = useState('');
  const [brandError, setBrandError] = useState('');
  const [modelValue, setModelValue] = useState('');
  const [modelError, setModelError] = useState('');

  const {
    isAdmin,
    isGroupManager,
    isCompanyManager,
    isBranchManager,
    isBranchAgent,
  } = useUserRole();

  const canAccessGroups = isAdmin || isGroupManager;
  const canAccessCompanies = isAdmin || isGroupManager || isCompanyManager;
  const canAccessBranches =
    isAdmin ||
    isGroupManager ||
    isCompanyManager ||
    isBranchManager ||
    isBranchAgent;

  const groupIsRequired = canAccessGroups;
  const companyIsRequired = !canAccessGroups && canAccessCompanies;
  const branchIsRequired =
    !canAccessGroups && !canAccessCompanies && canAccessBranches;

  /**
   * === Set Value Watchers section ===
   */
  const referenceNumberValue =
    watch &&
    watch('referenceNumber' as Path<TSchema>, data?.referenceNumber ?? '');

  const startDateValue =
    watch && watch('startDate' as Path<TSchema>, data?.startDate ?? null);

  const groupIdValue =
    watch && watch('groupId' as Path<TSchema>, data?.groupId ?? '');

  const companyIdValue =
    watch && watch('companyId' as Path<TSchema>, data?.companyId ?? '');

  const branchIdValue =
    watch && watch('branchId' as Path<TSchema>, data?.branchId ?? '');

  const vehiculeCheckTypeIdValue =
    watch &&
    watch(
      'vehiculeCheckTypeId' as Path<TSchema>,
      data?.vehiculeCheckTypeId ?? '',
    );

  const vehiculeIdValue =
    watch && watch('vehiculeId' as Path<TSchema>, data?.vehiculeId ?? '');

  const lastnameValue =
    watch &&
    watch(
      'individualLastname' as Path<TSchema>,
      data?.individualLastname ?? '',
    );

  const firstnameValue =
    watch &&
    watch(
      'individualFirstname' as Path<TSchema>,
      data?.individualFirstname ?? '',
    );

  const emailValue =
    watch &&
    watch('individualEmail' as Path<TSchema>, data?.individualEmail ?? '');

  const additionalEmailsValue =
    watch &&
    watch('additionalEmails' as Path<TSchema>, data?.additionalEmails ?? '');

  const phoneValue =
    watch &&
    watch('individualPhone' as Path<TSchema>, data?.individualPhone ?? '');

  const { data: existingVehicule } = useGetExistingVehicule({
    id: data?.id,
    ownerGroupId: groupIdValue,
    ownerCompanyId: companyIdValue,
    vin: vinValue,
    licensePlateNumber: licensePlateNumberValue,
  });

  // const emailIsToSendValue =
  //   watch &&
  //   watch('emailIsToSend' as Path<TSchema>, data?.emailIsToSend ?? false);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { getGroupsSelectOptions, isLoadingAllGroups } =
    useGetGroupsSelectOptions();

  const { getCompaniesSelectOptions, isLoadingAllCompanies } =
    useGetCompaniesSelectOptions({
      groupId: groupIdValue,
    });

  const { getBranchesSelectOptions, isLoadingAllBranches } =
    useGetBranchesSelectOptions({
      groupId: groupIdValue,
      companyId: companyIdValue,
    });

  const { getAllVehiculesSelectOptions, isLoadingAllVehicules } =
    useGetVehiculesSelectOptions({
      groupId: groupIdValue,
      companyId: companyIdValue,
    });

  const { getVehiculeCheckTypesSelectOptions, isLoadingAllVehiculeCheckTypes } =
    useGetVehiculeCheckTypesSelectOptions({
      groupId: groupIdValue,
      personTypes: [ActionUserType.INDIVIDUAL],
    });

  const numberOfCompanies = getCompaniesSelectOptions()?.length;
  const numberOfBranches = getBranchesSelectOptions()?.length;
  const numberOfVehicules = getAllVehiculesSelectOptions()?.length;
  const numberOfVehiculeCheckTypes =
    getVehiculeCheckTypesSelectOptions()?.length;

  const { mutate: mutateCreateVehicule, isPending: isPendingCreateVehicule } =
    useCreateVehicule({
      t,
      showDefaultSuccessMessage: false,
      handleSuccess: (data) => {
        setValue('vehiculeId' as Path<TSchema>, data?.['data']?.id);

        setLicensePlateNumberValue('');
        setVinValue('');
        setBrandValue('');
        setModelValue('');

        if (allFieldErrorsEmpty(errors)) {
          // onSubmit?.();
          showModal('#inspection-request-confirmation-modal');
        }
      },
    });

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (vehiculeId || data?.vehiculeId) {
      setValue(
        'vehiculeId' as Path<TSchema>,
        (vehiculeId || data?.vehiculeId) ?? '',
      );
    }
  }, [vehiculeId, data, setValue]);

  useEffect(() => {
    if (setValue && data) {
      setValue('referenceNumber' as Path<TSchema>, data.referenceNumber ?? '');
      setValue('startDate' as Path<TSchema>, data.startDate ?? '');
      setValue('groupId' as Path<TSchema>, data.groupId ?? '');
      setValue('companyId' as Path<TSchema>, data.companyId ?? '');
      setValue('branchId' as Path<TSchema>, data.branchId ?? '');
      setValue(
        'vehiculeCheckTypeId' as Path<TSchema>,
        data.vehiculeCheckTypeId ?? '',
      );

      setValue(
        'individualLastname' as Path<TSchema>,
        data.individualLastname ?? '',
      );

      setValue(
        'individualFirstname' as Path<TSchema>,
        data.individualFirstname ?? '',
      );

      setValue('individualEmail' as Path<TSchema>, data.individualEmail ?? '');

      setValue(
        'additionalEmails' as Path<TSchema>,
        Array.isArray(data.additionalEmails)
          ? data.additionalEmails.join('; ')
          : (data.additionalEmails ?? ''),
      );

      setValue('individualPhone' as Path<TSchema>, data.individualPhone ?? '');

      setValue(
        'individualAddressLine1' as Path<TSchema>,
        data.individualAddressLine1 ?? '',
      );

      setValue(
        'individualAddressLine2' as Path<TSchema>,
        data.individualAddressLine2 ?? '',
      );

      setValue(
        'individualPostalCode' as Path<TSchema>,
        data.individualPostalCode ?? '',
      );

      setValue('individualCity' as Path<TSchema>, data.individualCity ?? '');

      setValue(
        'individualCountry' as Path<TSchema>,
        data.individualCountry ?? '',
      );
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Generate UUID for Reference Number ===
   */
  useEffect(() => {
    const referenceNumber = data?.referenceNumber || generateRandomUUID();

    setValue(
      'referenceNumber' as Path<TSchema>,
      referenceNumber as PathValue<TSchema, Path<TSchema>>,
    );
  }, [data?.referenceNumber, setValue]);
  /**
   * === END OF Generate UUID for Reference Number ===
   */

  /**
   * === Vehicule Select Functionality section ===
   */
  useEffect(() => {
    if (!assignedToVehicule) {
      setValue(
        'vehiculeId' as Path<TSchema>,
        '' as PathValue<TSchema, Path<TSchema>>,
      );
    } else {
      if (vehiculeId || data?.vehiculeId) {
        setValue('vehiculeId' as Path<TSchema>, vehiculeId || data?.vehiculeId);
      }
      setLicensePlateNumberValue('');
      setVinValue('');
      setBrandValue('');
      setModelValue('');
    }
  }, [assignedToVehicule, setValue, vehiculeId, data?.vehiculeId]);
  /**
   * === END OF Vehicule Select Functionality section ===
   */

  /**
   * === Set default email to Additional Emails section ===
   */
  useEffect(() => {
    if (!data) {
      if (addAdditionalEmails) {
        setValue(
          'additionalEmails' as Path<TSchema>,
          [loggedInUser?.email ?? ''] as PathValue<TSchema, Path<TSchema>>,
        );
      } else {
        setValue(
          'additionalEmails' as Path<TSchema>,
          '' as PathValue<TSchema, Path<TSchema>>,
        );
      }
    }
  }, [data, addAdditionalEmails, loggedInUser, setValue]);
  /**
   * === END OF Set default email to Additional Emails section ===
   */

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (groupIdValue) {
      resetFieldError<TSchema>({ fieldName: 'groupId', setError });
    }

    if (companyIdValue) {
      resetFieldError<TSchema>({ fieldName: 'companyId', setError });
    }

    if (branchIdValue) {
      resetFieldError<TSchema>({ fieldName: 'branchId', setError });
    }

    if (referenceNumberValue) {
      resetFieldError<TSchema>({ fieldName: 'referenceNumber', setError });
    }

    if (startDateValue) {
      resetFieldError<TSchema>({ fieldName: 'startDate', setError });
    }

    if (vehiculeIdValue) {
      resetFieldError<TSchema>({ fieldName: 'vehiculeId', setError });
    }

    if (licensePlateNumberValue) {
      setLicensePlateNumberError('');
    }

    if (vinValue) {
      setVinError('');
    }

    if (brandValue) {
      setBrandError('');
    }

    if (modelValue) {
      setModelError('');
    }

    if (vehiculeCheckTypeIdValue) {
      resetFieldError<TSchema>({ fieldName: 'vehiculeCheckTypeId', setError });
    }

    if (lastnameValue) {
      resetFieldError<TSchema>({ fieldName: 'individualLastname', setError });
    }

    if (firstnameValue) {
      resetFieldError<TSchema>({ fieldName: 'individualFirstname', setError });
    }

    if (emailValue) {
      resetFieldError<TSchema>({ fieldName: 'individualEmail', setError });
    }

    if (additionalEmailsValue) {
      resetFieldError<TSchema>({ fieldName: 'additionalEmails', setError });
    }

    if (phoneValue) {
      resetFieldError<TSchema>({ fieldName: 'individualPhone', setError });
    }
  }, [
    groupIdValue,
    companyIdValue,
    branchIdValue,
    referenceNumberValue,
    startDateValue,
    licensePlateNumberValue,
    vinValue,
    brandValue,
    modelValue,
    vehiculeIdValue,
    vehiculeCheckTypeIdValue,
    lastnameValue,
    firstnameValue,
    emailValue,
    additionalEmailsValue,
    phoneValue,
    setError,
  ]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!referenceNumberValue) {
      setError('referenceNumber' as Path<TSchema>, {
        message: t('referenceNumberRequired'),
      });
    }

    if (!startDateValue) {
      setError('startDate' as Path<TSchema>, {
        message: t('startOfInspectionDateRequired'),
      });
    }

    if (groupIsRequired && !groupIdValue) {
      setError('groupId' as Path<TSchema>, {
        message: t('common:groupRequired'),
      });
    }

    if (companyIsRequired && !companyIdValue) {
      setError('companyId' as Path<TSchema>, {
        message: t('common:companyRequired'),
      });
    }

    if (branchIsRequired && !branchIdValue) {
      setError('branchId' as Path<TSchema>, {
        message: t('common:branchRequired'),
      });
    }

    if (!vehiculeCheckTypeIdValue) {
      setError('vehiculeCheckTypeId' as Path<TSchema>, {
        message: t('inspectionTypeRequired'),
      });
    }

    if (assignedToVehicule) {
      if (!vehiculeIdValue) {
        setError('vehiculeId' as Path<TSchema>, {
          message: t('common:vehiculeRequired'),
        });
      }
    } else {
      const vehiculeErrorMessages = existingVehicule?.data?.errorMessages;
      let hasLicensePlateError = false;
      let hasVinError = false;
      let hasBrandError = false;
      let hasModelError = false;

      if (licensePlateNumberValue) {
        if (!FRENCH_LICENSE_PLATE_NUMBER_REGEX.test(licensePlateNumberValue)) {
          setLicensePlateNumberError(
            t('common:licensePlateNumberFrenchIncorrect'),
          );
          hasLicensePlateError = true;
        } else if (
          vehiculeErrorMessages?.includes(
            FieldError.LICENSE_PLATE_NUMBER_WITH_GROUP_ID_EXISTS,
          )
        ) {
          setLicensePlateNumberError(
            t('licensePlateUsedBySameGroupChooseInsideFleet'),
          );
          hasLicensePlateError = true;
        } else if (
          vehiculeErrorMessages?.includes(
            FieldError.LICENSE_PLATE_NUMBER_WITH_COMPANY_ID_EXISTS,
          )
        ) {
          setLicensePlateNumberError(
            t('licensePlateUsedBySameCompanyChooseInsideFleet'),
          );
          hasLicensePlateError = true;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (vinValue) {
        if (!VIN_REGEX.test(vinValue)) {
          setVinError(t('common:vinIncorrect'));
          hasVinError = true;
        } else if (
          vehiculeErrorMessages?.includes(FieldError.VIN_WITH_GROUP_ID_EXISTS)
        ) {
          setVinError(t('vinUsedBySameGroupChooseInsideFleet'));
          hasVinError = true;
        } else if (
          vehiculeErrorMessages?.includes(FieldError.VIN_WITH_COMPANY_ID_EXISTS)
        ) {
          setVinError(t('vinUsedBySameCompanyChooseInsideFleet'));
          hasVinError = true;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (!brandValue) {
        setBrandError(t('common:brandRequired'));
        hasBrandError = true;

        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (!modelValue) {
        setModelError(t('common:modelRequired'));
        hasModelError = true;

        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (
        !hasLicensePlateError &&
        !hasVinError &&
        !hasBrandError &&
        !hasModelError
      ) {
        mutateCreateVehicule({
          licensePlateNumber: licensePlateNumberValue,
          vin: vinValue,
          brand: brandValue,
          model: modelValue,
          ownerGroupId: groupIdValue,
          ownerCompanyId: companyIdValue,
        });
      }
    }

    if (!lastnameValue) {
      setError('individualLastname' as Path<TSchema>, {
        message: t('common:lastnameRequired'),
      });
    }

    if (!firstnameValue) {
      setError('individualFirstname' as Path<TSchema>, {
        message: t('common:firstnameRequired'),
      });
    }

    if (!emailValue) {
      setError('individualEmail' as Path<TSchema>, {
        message: t('common:emailRequired'),
      });
    } else if (!EMAIL_REGEX.test(emailValue)) {
      setError('individualEmail' as Path<TSchema>, {
        message: t('common:emailFormatInvalid'),
      });
    }

    if (typeof additionalEmailsValue === 'string' && additionalEmailsValue) {
      const additionalEmails: string[] = additionalEmailsValue
        .split(/[,;]/)
        .map((email: string) => email.trim())
        .filter(Boolean);

      if (additionalEmails.some((email: string) => !EMAIL_REGEX.test(email))) {
        setError('additionalEmails' as Path<TSchema>, {
          message: t('addtionalEmailsIncorrect'),
        });
      }
    }

    if (phoneValue && !PHONE_REGEX.test(phoneValue)) {
      setError('individualPhone' as Path<TSchema>, {
        message: t('common:phoneNumberIncorrect'),
      });
    }

    if (assignedToVehicule && allFieldErrorsEmpty(errors)) {
      // onSubmit?.();
      showModal('#inspection-request-confirmation-modal');
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('common:informations')}
              defaultIconClassName="ki-badge"
            />
          }
          footer={
            !isReadOnly && (
              <Button
                buttonClassName="px-8"
                // text={
                //   submitDisabled ||
                //   isPendingCreateVehicule ||
                //   isPendingCreateVehicule
                //     ? `${t('common:loading')}...`
                //     : emailIsToSendValue
                //       ? t('common:send')
                //       : t('common:save')
                // }
                text={
                  submitDisabled || isPendingCreateVehicule
                    ? `${t('common:loading')}...`
                    : t('common:send')
                }
                disabled={submitDisabled || isPendingCreateVehicule}
              />
            )
          }
          bodyClassName="pt-8 pb-20 flex flex-col gap-5"
        >
          <>
            <FormSectionTitle
              title={t('theInspection')}
              containerClassName="mt-5"
              titleClassName="font-semibold"
              icon={
                <i className="h-0 ki-outline ki-notepad !text-[25px] -mt-0.75" />
              }
            />
            <FormField
              text={t('common:referenceNumber_short_2')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...register('referenceNumber' as Path<TSchema>, {
                    setValueAs: (value: string) =>
                      value.replace(SPACE_REGEX, ''),
                  })}
                  errorMessage={
                    errors && (errors.referenceNumber?.message as string)
                  }
                  // Enforce to input only value to be without space
                  onInput={(e) =>
                    (e.currentTarget.value = e.currentTarget.value.replace(
                      SPACE_REGEX,
                      '',
                    ))
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:referenceNumber_short_2')}
                  type="text"
                />
              </div>
            </FormField>
            <FormField
              text={t('startOfInspectionDate')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <div className="flex flex-1 flex-col gap-0.75">
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker<'startDate'>
                        field={field}
                        hasError={!!errors.startDate?.message}
                        variant="mobile"
                        useTime={false}
                        disabled={isReadOnly}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => {
                          if (date) {
                            const utcDate = new Date(
                              Date.UTC(date.year(), date.month(), date.date()),
                            );
                            field.onChange(utcDate);
                          } else {
                            field.onChange(null);
                          }
                        }}
                      />
                    )}
                  />
                  <ErrorMessage
                    message={errors && (errors.startDate?.message as string)}
                  />
                </div>
              </div>
            </FormField>
            {canAccessGroups && (
              <FormField
                text={t('common:group')}
                required={groupIsRequired}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllGroups ? (
                    <Select
                      placeholder={t('common:chooseTheGroup')}
                      options={[]}
                    />
                  ) : (
                    <Controller
                      name="groupId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.groupId?.message as string)
                          }
                          disabled={isReadOnly}
                          placeholder={t('common:chooseTheGroup')}
                          options={getGroupsSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            {canAccessCompanies && (
              <FormField
                text={t('common:company')}
                required={companyIsRequired}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllCompanies ? (
                    <Select
                      placeholder={t('common:chooseTheCompany')}
                      options={[]}
                    />
                  ) : !numberOfCompanies ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={t('common:noCompanyLinkedToThisGroup')}
                      />
                    </div>
                  ) : (
                    <Controller
                      name="companyId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.companyId?.message as string)
                          }
                          disabled={isReadOnly}
                          placeholder={t('common:chooseTheCompany')}
                          options={getCompaniesSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            {canAccessBranches && (
              <FormField
                text={t('common:branch')}
                required={branchIsRequired}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllBranches ? (
                    <Select
                      placeholder={t('common:chooseTheBranch')}
                      options={[]}
                    />
                  ) : !numberOfBranches ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={
                          groupIdValue
                            ? t('common:noBranchLinkedToThisGroup')
                            : t('common:noBranchLinkedToThisCompany')
                        }
                      />
                    </div>
                  ) : (
                    <Controller
                      name="branchId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.branchId?.message as string)
                          }
                          disabled={isReadOnly}
                          placeholder={t('common:chooseTheBranch')}
                          options={getBranchesSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            <FormField
              text={`${t('onAVehiculeOfTheFleet')} ?`}
              textWrapperClassName="mt-2"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                <Switch
                  disabled={isReadOnly}
                  onChange={() =>
                    setAssignedToVehicule((prevState) => !prevState)
                  }
                  checked={assignedToVehicule}
                />
              </div>
            </FormField>
            {assignedToVehicule ? (
              <FormField
                text={t('common:vehicule')}
                required={assignedToVehicule && numberOfVehicules > 0}
                textWrapperClassName="mt-5"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                  {isLoadingAllVehicules ? (
                    <Select
                      placeholder={t('common:chooseTheVehicule')}
                      options={[]}
                    />
                  ) : !numberOfVehicules ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={
                          branchIdValue
                            ? t('noVehiculeBelongsToThisBranch')
                            : companyIdValue
                              ? t('noVehiculeBelongsToThisCompany')
                              : t('noVehiculeBelongsToThisGroup')
                        }
                      />
                    </div>
                  ) : (
                    <Controller
                      name="vehiculeId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.vehiculeId?.message as string)
                          }
                          disabled={isReadOnly}
                          placeholder={t('common:chooseTheVehicule')}
                          options={getAllVehiculesSelectOptions()}
                        />
                      )}
                    />
                    // <SelectAutoComplete
                    //   placeholder={t('common:chooseTheVehicule')}
                    //   name="vehiculeId"
                    //   control={control}
                    //   options={getAllVehiculesSelectOptions() ?? []}
                    //   disabled={false}
                    //   errorMessage={
                    //     errors && (errors.vehiculeId?.message as string)
                    //   }
                    // />
                  )}
                </div>
              </FormField>
            ) : (
              <>
                <FormField
                  text={t('common:licensePlateNumber')}
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={licensePlateNumberValue}
                      onChange={(e) =>
                        setLicensePlateNumberValue(e.target.value)
                      }
                      errorMessage={licensePlateNumberError}
                      disabled={isReadOnly}
                      placeholder={t('common:licensePlateNumber')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField text={t('common:vin')} textWrapperClassName="mt-3">
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={vinValue}
                      onChange={(e) => setVinValue(e.target.value)}
                      errorMessage={vinError}
                      disabled={isReadOnly}
                      placeholder={t('common:vin')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField
                  text={t('common:brand')}
                  required
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={brandValue}
                      onChange={(e) => setBrandValue(e.target.value)}
                      errorMessage={brandError}
                      disabled={isReadOnly}
                      placeholder={t('common:brand')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField
                  text={t('common:model')}
                  required
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={modelValue}
                      onChange={(e) => setModelValue(e.target.value)}
                      errorMessage={modelError}
                      disabled={isReadOnly}
                      placeholder={t('common:model')}
                      type="text"
                    />
                  </div>
                </FormField>
              </>
            )}
            <FormField
              text={`${t('whatTypeOfInspectionToPerform')} ?`}
              required
              containerClassName="mt-7"
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                {!isLoadingAllVehiculeCheckTypes &&
                !numberOfVehiculeCheckTypes ? (
                  <div className="mt-3">
                    <NotFound
                      containerClassName="gap-1.75"
                      iconClassName="-mt-1.25 text-xl"
                      textClassName="text-sm"
                      text={`${
                        groupIdValue
                          ? t('noInspectionTypeLinkedToThisGroup')
                          : t('youAreNotLinkedToAnyInspectionType')
                      } ${errors.vehiculeCheckTypeId?.message ? `. ${t('whileInspectionTypeIsRequired')}` : ''}`}
                    />
                  </div>
                ) : (
                  <Controller
                    name="vehiculeCheckTypeId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        errorMessage={
                          errors &&
                          (errors.vehiculeCheckTypeId?.message as string)
                        }
                        disabled={isReadOnly}
                        placeholder={t('common:chooseTheInspectionType')}
                        options={getVehiculeCheckTypesSelectOptions()}
                      />
                    )}
                  />
                )}
              </div>
            </FormField>
            <hr className="mt-7 mb-5" />
            <FormSectionTitle
              title={t('clientInformations')}
              titleClassName="font-semibold"
              icon={
                <i className="h-0 ki-outline ki-user-tick !text-[30px] -mt-1.5" />
              }
            />
            <FormField
              text={t('common:lastName')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualLastname' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualLastname?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:lastName')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:firstName')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualFirstname' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualFirstname?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:firstName')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:email')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualEmail' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualEmail?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:email')}
                  type="email"
                />
              </div>
            </FormField>
            <FormField
              text={`${t('addOtherReceiverEmails')} ?`}
              textWrapperClassName="mt-2"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                <Switch
                  disabled={isReadOnly}
                  onChange={() =>
                    setAddAdditionalEmails((prevState) => !prevState)
                  }
                  checked={addAdditionalEmails}
                />
              </div>
            </FormField>
            {addAdditionalEmails && (
              <FormField
                text={t('additionalEmails')}
                textWrapperClassName="mt-3"
                subText={t('additionalEmailsSubText')}
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  <Textarea
                    {...(register &&
                      register('additionalEmails' as Path<TSchema>))}
                    errorMessage={
                      errors && (errors.additionalEmails?.message as string)
                    }
                    placeholder={t('additionalEmails')}
                  />
                </div>
              </FormField>
            )}
            <FormField
              text={t('common:phoneNumber')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualPhone' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualPhone?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:phoneNumber')}
                  type="tel"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:addressLine1')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualAddressLine1' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualAddressLine1?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:addressLine1')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:addressLine2')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualAddressLine2' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualAddressLine2?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:addressLine2')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField
              text={t('common:postalCode')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualPostalCode' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualPostalCode?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:postalCode')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField text={t('common:city')} textWrapperClassName="mt-3">
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register && register('individualCity' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualCity?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:city')}
                  type="search"
                />
              </div>
            </FormField>
            <FormField text={t('common:country')} textWrapperClassName="mt-3">
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  {...(register &&
                    register('individualCountry' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.individualCountry?.message as string)
                  }
                  disabled={isReadOnly}
                  placeholder={t('common:country')}
                  type="search"
                />
              </div>
            </FormField>
            {/* <hr className="mt-7 mb-5" />
            <FormSectionTitle
              title={t('sendingClientRequestByEmail')}
              titleClassName="font-semibold"
            />
            <FormField
              text={`${t('sendTheRequestByEmail')} ?`}
              textWrapperClassName="mt-2"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Switch
                  {...(register && register('emailIsToSend' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.emailIsToSend?.message as string)
                  }
                  checked={emailIsToSendValue}
                />
              </div>
            </FormField> */}
          </>
        </Card>
      </form>
      <ConfirmationModal
        id="inspection-request-confirmation-modal"
        title={t('common:confirmation')}
        description={
          <Trans
            i18nKey={`${t('areYouSureToSendThisRequestByEmail')} ?`}
            components={{ br: <br /> }}
          />
        }
        onCancelLabel={t('common:no')}
        onConfirmLabel={t('common:yes')}
        onConfirm={() => {
          onSubmit?.();
        }}
      />

      {isPendingCreateVehicule && <Loader />}
    </>
  );
};
